import { useState } from 'react';

export const useProductosModificados = () => {
  const [productosModificados, setProductosModificados] = useState({});
  const [competenciasAgregadas, setCompetenciasAgregadas] = useState({});
  const [competenciasEliminadas, setCompetenciasEliminadas] = useState({});
  const [preciosCompetenciasModificados, setPreciosCompetenciasModificados] = useState({});
  const [guardando, setGuardando] = useState(false);

  // Función para manejar cambios en unidad
  const handleUnidadChange = (productoId, nuevaUnidad) => {
    setProductosModificados(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        unit: nuevaUnidad
      }
    }));
  };

  // Función para manejar cambios en cantidad
  const handleCantidadChange = (productoId, nuevaCantidad) => {
    setProductosModificados(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        qty: parseFloat(nuevaCantidad) || 0
      }
    }));
  };

  // Función para manejar cambios en precio
  const handlePrecioChange = (productoId, nuevoPrecio) => {
    setProductosModificados(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        precio: parseFloat(nuevoPrecio) || 0
      }
    }));
  };

  // Función para manejar cambios en moneda
  const handleMonedaChange = (productoId, nuevaMoneda) => {
    setProductosModificados(prev => ({
      ...prev,
      [productoId]: {
        ...prev[productoId],
        moneda: nuevaMoneda
      }
    }));
  };

  // Función para manejar cambios en competencias
  const handleCompetenciaChange = (productoId, competenciaIndex, nuevoSku, setProductosData, selectedClient, opcionesDisponibles) => {
    // Buscar la competencia seleccionada para obtener su QTY
    const competenciaSeleccionada = opcionesDisponibles?.find(opcion => opcion.value === nuevoSku);
    const cantidadQTY = competenciaSeleccionada?.cantidad;
    
    // Actualizar el estado local del producto
    if (selectedClient) {
      setProductosData(prev => {
        const clienteId = selectedClient.id;
        const productosCliente = prev[clienteId] || [];
        
        const productoIndex = productosCliente.findIndex(p => p.id === productoId);
        if (productoIndex === -1) return prev;
        
        const nuevosProductos = [...productosCliente];
        const nuevasCompetencias = [...(nuevosProductos[productoIndex].competencias || [])];
        
        // Actualizar la competencia en el índice especificado
        if (nuevasCompetencias[competenciaIndex]) {
          nuevasCompetencias[competenciaIndex] = {
            ...nuevasCompetencias[competenciaIndex],
            sku: nuevoSku,
            // Auto-llenar cantidad con QTY si tiene valor, sino dejarlo vacío
            cantidad: cantidadQTY !== null && cantidadQTY !== undefined ? cantidadQTY : ""
          };
        }
        
        nuevosProductos[productoIndex] = {
          ...nuevosProductos[productoIndex],
          competencias: nuevasCompetencias
        };
        
        return {
          ...prev,
          [clienteId]: nuevosProductos
        };
      });
    }
    
    // Registrar la competencia para insertar
    if (nuevoSku) {
      setCompetenciasAgregadas(prev => ({
        ...prev,
        [productoId]: [
          ...(prev[productoId] || []),
          {
            index: competenciaIndex,
            competenceId: parseInt(nuevoSku)
          }
        ]
      }));
    }
  };

  // Función para manejar cambios en cantidad de competencias
  const handleCompetenciaCantidadChange = (productoId, competenciaIndex, nuevaCantidad, setProductosData, selectedClient) => {
    // Actualizar el estado local del producto
    if (selectedClient) {
      setProductosData(prev => {
        const clienteId = selectedClient.id;
        const productosCliente = prev[clienteId] || [];
        
        const productoIndex = productosCliente.findIndex(p => p.id === productoId);
        if (productoIndex === -1) return prev;
        
        const nuevosProductos = [...productosCliente];
        const nuevasCompetencias = [...(nuevosProductos[productoIndex].competencias || [])];
        
        // Actualizar la cantidad de la competencia en el índice especificado
        if (nuevasCompetencias[competenciaIndex]) {
          nuevasCompetencias[competenciaIndex] = {
            ...nuevasCompetencias[competenciaIndex],
            cantidad: parseFloat(nuevaCantidad) || 0
          };
        }
        
        nuevosProductos[productoIndex] = {
          ...nuevosProductos[productoIndex],
          competencias: nuevasCompetencias
        };
        
        return {
          ...prev,
          [clienteId]: nuevosProductos
        };
      });
    }
  };

  // Función para manejar cambios en precio de competencias
  const handleCompetenciaPrecioChange = (productoId, competenciaIndex, nuevoPrecio, setProductosData, selectedClient) => {
    // Actualizar el estado local del producto
    if (selectedClient) {
      setProductosData(prev => {
        const clienteId = selectedClient.id;
        const productosCliente = prev[clienteId] || [];
        
        const productoIndex = productosCliente.findIndex(p => p.id === productoId);
        if (productoIndex === -1) return prev;
        
        const nuevosProductos = [...productosCliente];
        const nuevasCompetencias = [...(nuevosProductos[productoIndex].competencias || [])];
        
        // Actualizar el precio de la competencia en el índice especificado
        if (nuevasCompetencias[competenciaIndex]) {
          const competenciaSku = nuevasCompetencias[competenciaIndex].sku;
          
          // Registrar el cambio de precio en el estado separado
          if (competenciaSku) {
            setPreciosCompetenciasModificados(prev => ({
              ...prev,
              [competenciaSku]: parseFloat(nuevoPrecio) || 0
            }));
          }
          
          nuevasCompetencias[competenciaIndex] = {
            ...nuevasCompetencias[competenciaIndex],
            precio: parseFloat(nuevoPrecio) || 0
          };
        }
        
        nuevosProductos[productoIndex] = {
          ...nuevosProductos[productoIndex],
          competencias: nuevasCompetencias
        };
        
        return {
          ...prev,
          [clienteId]: nuevosProductos
        };
      });
    }
  };

  // Función para obtener el total de cambios pendientes
  const obtenerTotalCambiosPendientes = () => {
    const totalProductosModificados = Object.keys(productosModificados).length;
    const totalCompetenciasAgregadas = Object.values(competenciasAgregadas).reduce((total, competencias) => total + competencias.length, 0);
    const totalCompetenciasEliminadas = Object.values(competenciasEliminadas).reduce((total, competencias) => total + competencias.length, 0);
    
    return {
      total: totalProductosModificados + totalCompetenciasAgregadas + totalCompetenciasEliminadas,
      productos: totalProductosModificados,
      competenciasAgregadas: totalCompetenciasAgregadas,
      competenciasEliminadas: totalCompetenciasEliminadas
    };
  };

  // Función para limpiar todos los cambios
  const limpiarCambios = () => {
    setProductosModificados({});
    setCompetenciasAgregadas({});
    setCompetenciasEliminadas({});
    setPreciosCompetenciasModificados({});
  };

  return {
    productosModificados,
    competenciasAgregadas,
    competenciasEliminadas,
    preciosCompetenciasModificados,
    guardando,
    setGuardando,
    handleUnidadChange,
    handleCantidadChange,
    handlePrecioChange,
    handleMonedaChange,
    handleCompetenciaChange,
    handleCompetenciaCantidadChange,
    handleCompetenciaPrecioChange,
    obtenerTotalCambiosPendientes,
    limpiarCambios,
    setCompetenciasAgregadas,
    setCompetenciasEliminadas
  };
};
